import React, { useEffect, useState } from "react";
import ImageUploadField from "../../components/admin/ImageUploadField";
import {
  createAdvertisement,
  deleteAdvertisement,
  fetchAdvertisements,
  updateAdvertisement,
} from "../../services/promotionApi";
import { uploadImage } from "../../services/uploadApi";
import TableScroll from "../../components/TableScroll";
import "../../styles/admin-common.css";

const emptyForm = {
  title: "",
  imageUrl: "",
  linkType: "NONE",
  linkTarget: "",
  isActive: true,
  displayOrder: 0,
};

const ALLOWED_ROUTES = [
  { value: "/product-type", label: "Sell flow — choose category" },
  { value: "/my-coupons", label: "My coupons" },
  { value: "/lucky-draw-play", label: "Scratch & win (first available card)" },
  { value: "/help-center", label: "Help center" },
  { value: "/faq", label: "FAQ" },
  { value: "/terms-conditions", label: "Terms & conditions" },
  { value: "/privacy-policy", label: "Privacy policy" },
  { value: "/about-us", label: "About us" },
  { value: "/notifications", label: "Notifications" },
  { value: "/settings", label: "Settings" },
  { value: "/search", label: "Search" },
  { value: "/contact-support", label: "Contact support" },
];

const Advertisements = () => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setItems(await fetchAdvertisements());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const reset = () => {
    setForm(emptyForm);
    setEditingId(null);
    setPendingFile(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    let imageUrl = form.imageUrl?.trim() || "";
    try {
      if (pendingFile) {
        setUploading(true);
        try {
          const uploaded = await uploadImage(pendingFile, "promo-banners");
          imageUrl = uploaded.url;
        } finally {
          setUploading(false);
        }
      }
      if (!imageUrl) {
        setError("Upload a banner image or paste an image URL.");
        return;
      }
      const payload = {
        ...form,
        imageUrl,
        linkTarget: form.linkType === "NONE" ? null : (form.linkTarget?.trim() || null),
        displayOrder: Number(form.displayOrder) || 0,
      };
      if (payload.linkType === "URL" && payload.linkTarget && !/^https:\/\//i.test(payload.linkTarget)) {
        setError("External links must start with https://");
        return;
      }
      if ((payload.linkType === "URL" || payload.linkType === "ROUTE") && !payload.linkTarget) {
        setError("Choose a link target for this advertisement.");
        return;
      }
      if (editingId) await updateAdvertisement(editingId, payload);
      else await createAdvertisement(payload);
      reset();
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setPendingFile(null);
    setForm({
      title: item.title || "",
      imageUrl: item.imageUrl || "",
      linkType: item.linkType || "NONE",
      linkTarget: item.linkTarget || "",
      isActive: item.isActive !== false,
      displayOrder: item.displayOrder ?? 0,
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this advertisement?")) return;
    try {
      await deleteAdvertisement(id);
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="admin-page">
      <h2>Advertisements</h2>
      <p className="subtitle">Horizontal promo banners on the app home screen. Inactive ads are hidden from users.</p>
      {error && <p className="error-msg">{error}</p>}

      <div className="admin-split">
        <div className="admin-card">
          <h3>{editingId ? "Edit ad" : "Create ad"}</h3>
          <form className="admin-form" onSubmit={handleSubmit}>
            <label className="field-label">Title<input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required /></label>
            <ImageUploadField
              label="Banner image"
              imageUrl={form.imageUrl}
              onImageUrlChange={(url) => setForm({ ...form, imageUrl: url })}
              pendingFile={pendingFile}
              onPendingFileChange={setPendingFile}
              uploading={uploading}
              folderHint="Home carousel · uploads on save"
            />
            <label className="field-label">
              Link type
              <select
                value={form.linkType}
                onChange={(e) => setForm({
                  ...form,
                  linkType: e.target.value,
                  linkTarget: e.target.value === "NONE" ? "" : form.linkTarget,
                })}
              >
                <option value="NONE">None (display only)</option>
                <option value="URL">External https URL</option>
                <option value="ROUTE">In-app screen</option>
              </select>
            </label>
            {form.linkType === "URL" && (
              <label className="field-label">
                HTTPS URL
                <input
                  value={form.linkTarget}
                  onChange={(e) => setForm({ ...form, linkTarget: e.target.value })}
                  placeholder="https://nexonicetch.com/..."
                  required
                />
              </label>
            )}
            {form.linkType === "ROUTE" && (
              <label className="field-label">
                In-app destination
                <select
                  value={form.linkTarget}
                  onChange={(e) => setForm({ ...form, linkTarget: e.target.value })}
                  required
                >
                  <option value="">Select a screen…</option>
                  {ALLOWED_ROUTES.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </label>
            )}
            <label className="field-label">Display order<input type="number" value={form.displayOrder} onChange={(e) => setForm({ ...form, displayOrder: e.target.value })} /></label>
            <label className="checkbox-field"><input type="checkbox" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} /> Active</label>
            <div className="form-actions">
              <button type="submit" className="admin-btn" disabled={uploading}>{editingId ? "Update" : "Create"}</button>
              {editingId && <button type="button" className="admin-btn secondary" onClick={reset}>Cancel</button>}
            </div>
          </form>
        </div>

        <div className="admin-card">
          <h3>All advertisements</h3>
          {loading ? <div className="admin-loading">Loading…</div> : (
            <TableScroll>
              <table className="admin-table">
                <thead><tr><th>Title</th><th>Link</th><th>Order</th><th>Active</th><th></th></tr></thead>
                <tbody>
                  {items.length === 0 && <tr><td colSpan={5} className="empty-msg">No ads yet</td></tr>}
                  {items.map((item) => (
                    <tr key={item.id}>
                      <td>{item.title}</td>
                      <td>{item.linkType === "NONE" ? "—" : item.linkTarget}</td>
                      <td>{item.displayOrder}</td>
                      <td className={item.isActive ? "badge-active" : "badge-inactive"}>{item.isActive ? "Yes" : "No"}</td>
                      <td className="actions-cell">
                        <button type="button" className="admin-btn secondary" onClick={() => startEdit(item)}>Edit</button>
                        <button type="button" className="admin-btn danger" onClick={() => handleDelete(item.id)}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroll>
          )}
        </div>
      </div>
    </div>
  );
};

export default Advertisements;
